import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { ClassNavigation } from '../components/layout';
import ActionableSuggestions from '../components/ActionableSuggestions';
import { BackButton } from '../components/layout';

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
  studentCount: number;
  feedbackSetup: boolean;
  lastActivity: string;
}


const ClassDetails = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClassData = async () => {
      if (!classId || !user) return;

      setLoading(true);
      try {
        // Load class basic info
        const { data: classData } = await apiService.getClassById(classId);
        
        if (classData) {
          // Get actual student count from enrollments
          const { data: enrollments } = await apiService.getClassStudents(classId);
          const studentCount = enrollments ? enrollments.length : 0;
          
          // Check if feedback setup exists
          const { data: setup } = await apiService.getClassSetup(classId);
          const feedbackSetup = !!setup;
          
          // Get student profiles to find most recent activity
          const { data: studentsData } = await apiService.getStudentProfilesByClass(classId);
          let lastActivity = 'No recent activity';
          
          if (studentsData && studentsData.length > 0) {
            // Find most recent activity
            const mostRecentProfile = studentsData
              .filter(e => e.studentProfile?.updatedAt)
              .sort((a, b) => new Date(b.studentProfile!.updatedAt!).getTime() - new Date(a.studentProfile!.updatedAt!).getTime())[0];
            
            if (mostRecentProfile && mostRecentProfile.studentProfile) {
              const daysSince = Math.floor((Date.now() - new Date(mostRecentProfile.studentProfile.updatedAt!).getTime()) / (1000 * 60 * 60 * 24));
              lastActivity = daysSince === 0 ? 'Today' : 
                           daysSince === 1 ? '1 day ago' : 
                           `${daysSince} days ago`;
            }
          }
          
          const info: ClassInfo = {
            id: classData.id,
            name: classData.name,
            code: classData.code,
            term: classData.term,
            studentCount,
            feedbackSetup,
            lastActivity
          };
          
          setClassInfo(info);
        }
      } catch (error) {
        console.error('Failed to load class data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [classId, user]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="max-w-7xl mx-auto">
        <BackButton />
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900">Class not found</h2>
          <p className="text-gray-600 mt-2">The requested class could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ClassNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Class Header */}
      <div className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-xl border border-emerald-100">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{classInfo.name}</h1>
            <div className="flex items-center space-x-4 text-gray-600">
              <span className="font-medium">{classInfo.code}</span>
              <span>•</span>
              <span>{classInfo.term}</span>
              <span>•</span>
              <span>{classInfo.studentCount} Students</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">Last activity: {classInfo.lastActivity}</p>
          </div>
          <div className="text-right">
            {classInfo.feedbackSetup && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ Feedback Active
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Main Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div
          className="relative group cursor-pointer"
          onClick={() => navigate(`/dashboard/class/${classId}/insights`)}
        >
          <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">📊 Classroom Insights</h2>
              <div className="text-emerald-600">→</div>
            </div>
            <p className="text-gray-600 mb-4">
              View analytics, engagement metrics, and student progress.
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>• Performance Metrics</span>
              <span>• Student Profiles</span>
            </div>
          </div>
        </div>

        <div
          className="relative group cursor-pointer"
          onClick={() => navigate(`/dashboard/class/${classId}/feedback`)}
        >
          <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">📝 Feedback & Setup</h2>
              <div className="text-orange-600">→</div>
            </div>
            <p className="text-gray-600 mb-4">
              Configure surveys, view reports, and analyze responses.
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>• {classInfo.feedbackSetup ? 'Manage' : 'Setup'} Feedback</span>
              <span>• Response Analysis</span>
            </div>
          </div>
        </div>
      </div>

        <ActionableSuggestions classId={classId || ''} />
      </div>
    </div>
  );
};

export default ClassDetails;